import React, { useEffect, useState } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import SphereIdentityAtom from '../../../atoms/sphere-identity/SphereIdentityAtom';
import { useGetSphereInfoQuery } from '@center-pwa/store/sphereInfoAPI';
import './AtlasSphereIdentityMolecule.scss';
import { ProgressSpinner } from 'primereact/progressspinner';

interface AtlasSphereIdentityMoleculeProps {
  sphereId: string;
  className?: string;
  style?: React.CSSProperties;
}

const AtlasSphereIdentityMolecule: React.FC<
  AtlasSphereIdentityMoleculeProps
> = ({ sphereId, className = '', style = {} }) => {
  const { getEffectiveToken } = useImpersonation();
  const [token, setToken] = useState<string | null>(null);

  // Fetch the effective token (impersonation-aware) for the RTK Query call.
  // Using getEffectiveToken instead of getAccessToken so the impersonation
  // path is honored when active; falls back to B2C otherwise.
  useEffect(() => {
    (async () => {
      const t = await getEffectiveToken();
      setToken(t ?? null);
    })();
  }, [getEffectiveToken]);

  // 2) Determine if we should skip the RTK Query request
  const skip = !token; // skip if token is null

  // 3) Always call the hook, but pass `skip` to conditionally disable it
  const {
    data: sphereInfo,
    isLoading,
    isError,
  } = useGetSphereInfoQuery(
    { sphereId, token: token || '' }, // your query arg
    { skip } // the skip option
  );
  const sphere = sphereInfo?.sphere;

  // 4) Handle loading states
  //    - If skip=true (i.e., token not yet ready), we might want a separate loading UI
  if (!token) {
    return (
      <ProgressSpinner
        className="custom-spinner"
        style={{
          width: '20px',
          height: '20px',
          marginLeft: '10px',
          marginTop: '5px',
        }}
        strokeWidth="4"
      />
    );
  }
  if (isLoading) {
    return (
      <ProgressSpinner
        className="custom-spinner"
        style={{
          width: '20px',
          height: '20px',
          marginLeft: '10px',
          marginTop: '5px',
        }}
        strokeWidth="4"
      />
    );
  }
  if (isError || !sphereInfo) {
    return (
      <div className={`atlas-sphere-identity-molecule ${className}`}>
        Error...
      </div>
    );
  }

  // 5) Render the data
  return (
    <div
      className={`atlas-sphere-identity-molecule ${className}`}
      style={style}
    >
      <SphereIdentityAtom
        glyph={sphere.properties.glyph || 'default-icon'}
        glyphColor={sphere.properties.glyphColor}
        sphereId={sphere.id}
        sphereName={sphere.properties?.name || 'Unknown Sphere'}
        imageId={sphere.properties.imageId}
        size="medium"
      />
    </div>
  );
};

export default AtlasSphereIdentityMolecule;
